import { Link } from "react-router-dom";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import TextField from "@/component/widgets/text-field";
import { AppContextType, WonmovContext } from "@/context/app-context";
import { userRepo } from "@/repo/user-repo";

const OTP_LENGTH = 6;

type Step = "request" | "reset";

function OtpInputs({
  digits,
  onDigitChange,
  onKeyDown,
  onPaste,
  inputRefs,
}: {
  digits: string[];
  onDigitChange: (index: number, value: string) => void;
  onKeyDown: (index: number, e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  inputRefs: React.MutableRefObject<Array<HTMLInputElement | null>>;
}) {
  return (
    <div className="auth-otp">
      <div dir="ltr">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digit}
            onChange={(e) => onDigitChange(index, e.target.value)}
            onKeyDown={(e) => onKeyDown(index, e)}
            onPaste={onPaste}
            className="auth-otp__input"
            placeholder="_"
            aria-label={`رقم ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function ForgotPasswordPage() {
  const history = useHistory();
  const { setNotification } = useContext<AppContextType>(WonmovContext);

  const [step, setStep] = useState<Step>("request");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [digits, setDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const code = digits.join("");
  const isOtpComplete = code.length === OTP_LENGTH && digits.every((digit) => digit !== "");

  useEffect(() => {
    if (step !== "reset") return;
    inputRefs.current[0]?.focus();
  }, [step]);

  const updateDigit = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;

    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((char, index) => {
      next[index] = char;
    });
    setDigits(next);

    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const sendOtp = async () => {
    const trimmedUsername = username.trim();
    if (!trimmedUsername) {
      setNotification({ type: "warning", message: "نام کاربری را وارد کنید." });
      return;
    }
    if (isLoading) return;

    setIsLoading(true);
    const res = await userRepo.sendResetPasswordOtp(trimmedUsername);
    setIsLoading(false);

    if (!res.status) {
      setNotification({ type: "danger", message: res.message });
      return;
    }

    setUsername(trimmedUsername);
    setDigits(Array(OTP_LENGTH).fill(""));
    setStep("reset");
    setNotification({ type: "success", message: "کد تأیید ارسال شد." });
  };

  const resetPassword = async () => {
    if (!isOtpComplete) {
      setNotification({ type: "warning", message: "کد شش‌رقمی را کامل وارد کنید." });
      return;
    }
    if (!password) {
      setNotification({ type: "warning", message: "رمز عبور جدید را وارد کنید." });
      return;
    }
    if (password !== confirmPassword) {
      setNotification({ type: "warning", message: "رمز عبور و تکرار آن یکسان نیست." });
      return;
    }
    if (isLoading) return;

    setIsLoading(true);
    const res = await userRepo.resetPassword(username, code, password);
    setIsLoading(false);

    if (!res.status) {
      setNotification({ type: "danger", message: res.message });
      return;
    }

    setNotification({ type: "success", message: "رمز عبور با موفقیت تغییر کرد." });
    history.replace("/auth");
  };

  return (
    <div className="auth-card">
      <span className="auth-card__logo">WONMOV</span>

      <div className="auth-stack">
        <h2 className="auth-title">فراموشی رمز عبور</h2>

        {step === "request" ? (
          <>
            <p className="auth-text">
              نام کاربری خود را وارد کنید تا کد شش‌رقمی بازیابی رمز برای شما ارسال شود.
            </p>
            <TextField
              name="username"
              type="text"
              placeholder="نام کاربری"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <button
              type="button"
              disabled={isLoading}
              className="auth-submit"
              onClick={sendOtp}
            >
              {isLoading ? <div className="loader" /> : "ارسال کد تأیید"}
            </button>
          </>
        ) : (
          <>
            <p className="auth-text">
              کد شش‌رقمی ارسال‌شده برای{" "}
              <strong>{username}</strong> را وارد کنید و رمز عبور جدید را تنظیم کنید.
            </p>

            <OtpInputs
              digits={digits}
              onDigitChange={updateDigit}
              onKeyDown={handleOtpKeyDown}
              onPaste={handleOtpPaste}
              inputRefs={inputRefs}
            />

            <TextField
              name="password"
              type="password"
              placeholder="رمز عبور جدید"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              name="confirm-password"
              type="password"
              placeholder="تکرار رمز عبور جدید"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <button
              type="button"
              disabled={isLoading}
              className="auth-submit"
              onClick={resetPassword}
            >
              {isLoading ? <div className="loader" /> : "تغییر رمز عبور"}
            </button>

            <button
              type="button"
              disabled={isLoading}
              className="auth-link auth-link--inline"
              onClick={sendOtp}
            >
              ارسال مجدد کد
            </button>

            <button
              type="button"
              className="auth-link auth-link--inline"
              onClick={() => {
                setStep("request");
                setDigits(Array(OTP_LENGTH).fill(""));
                setPassword("");
                setConfirmPassword("");
              }}
            >
              ویرایش نام کاربری
            </button>
          </>
        )}

        <Link to="/auth" className="auth-link">
          بازگشت به ورود
        </Link>
      </div>
    </div>
  );
}
